import React from 'react';
import { Heart, Home, Users, MapPin, Star } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="About Ved Homestays — Authentic Banarasi Homestay Experience in Varanasi"
        description="Ved Homestays offers authentic Banarasi hospitality near Kashi Vishwanath Temple. Experience Varanasi like a local — warm hospitality, prime location, modern comfort. Learn why we are the best homestay in Varanasi."
        keywords="about ved homestays, varanasi homestay experience, banarasi hospitality, accommodation near kashi vishwanath temple, varanasi guesthouse, authentic varanasi stay, best homestay varanasi, live like a local varanasi, varanasi travel accommodation"
        url="https://www.vedhomestays.com/about"
        breadcrumbs={[
          { name: "Home", url: "https://www.vedhomestays.com/" },
          { name: "About Us", url: "https://www.vedhomestays.com/about" }
        ]}
      />
      
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1665413793087-d58c23e3a177"
            alt="Ganga Aarti"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">About Ved Homestays Varanasi</h1>
          <p className="text-xl md:text-2xl text-orange-300">Live in Banaras like a Banarasi — Authentic Homestay Experience</p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-800 mb-6 text-center">Our Story</h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                Welcome to Ved Homestays, where ancient traditions meet modern comfort in the heart of Varanasi. 
                Our journey began with a simple dream - to share the authentic essence of Banaras with travelers 
                from around the world.
              </p>
              <p>
                Located just a stone's throw away from the sacred Kashi Vishwanath Temple and the mesmerizing 
                Ganga Aarti ghats, Ved Homestays is more than just a place to stay - it's your home away from home 
                in this spiritual city. We believe that true travel experiences come from living like locals, 
                understanding their culture, and becoming part of their community.
              </p>
              <p>
                Our name "Ved" is derived from the ancient Vedas, symbolizing knowledge, wisdom, and timeless 
                traditions. We strive to embody these values by offering you not just accommodation, but a 
                gateway to experience the real Varanasi - the morning prayers, the evening aartis, the narrow 
                lanes filled with centuries of stories, and the warmth of Banarasi hospitality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-gradient-to-b from-orange-50 to-amber-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">What We Believe In</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Warm Hospitality</h3>
              <p className="text-gray-600">
                We treat every guest as part of our family, offering warmth and care that makes you feel at home.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Home className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Authentic Experience</h3>
              <p className="text-gray-600">
                Experience Varanasi beyond the tourist trails - live like a true Banarasi during your stay.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Prime Location</h3>
              <p className="text-gray-600">
                Perfectly situated near Kashi Vishwanath Temple and Ganga ghats for easy access to spiritual sites.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Quality Comfort</h3>
              <p className="text-gray-600">
                Clean, comfortable rooms with modern amenities ensuring a pleasant and memorable stay.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Why Choose Ved Homestays?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-orange-600 font-bold text-xl">1</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Unbeatable Location</h3>
                  <p className="text-gray-600">
                    Walk to Kashi Vishwanath Temple in minutes. Experience Ganga Aarti without the hassle 
                    of long commutes. Everything Varanasi offers is at your doorstep.
                  </p>
                </div>
              </div>
              <div className="flex space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-orange-600 font-bold text-xl">2</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Local Insights</h3>
                  <p className="text-gray-600">
                    Get insider tips about the best local eateries, hidden temples, morning boat rides, 
                    and experiences that guidebooks don't mention.
                  </p>
                </div>
              </div>
              <div className="flex space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-orange-600 font-bold text-xl">3</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Comfortable Stays</h3>
                  <p className="text-gray-600">
                    From solo travelers to large groups, we have rooms for everyone. Clean, well-maintained, 
                    and equipped with all modern amenities.
                  </p>
                </div>
              </div>
              <div className="flex space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-orange-600 font-bold text-xl">4</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Value for Money</h3>
                  <p className="text-gray-600">
                    Competitive pricing with no hidden charges. What you see is what you pay. 
                    Quality accommodation at honest prices.
                  </p>
                </div>
              </div>
              <div className="flex space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-orange-600 font-bold text-xl">5</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">24/7 Support</h3>
                  <p className="text-gray-600">
                    Whether you arrive at dawn or midnight, we're here for you. Round-the-clock assistance 
                    to make your stay hassle-free.
                  </p>
                </div>
              </div>
              <div className="flex space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-orange-600 font-bold text-xl">6</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Cultural Immersion</h3>
                  <p className="text-gray-600">
                    Experience daily life in Varanasi - from morning rituals to evening prayers. 
                    We help you connect with the soul of the city.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Banarasi Experience */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Users size={64} className="mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">The Banarasi Experience</h2>
            <p className="text-xl leading-relaxed mb-8">
              "To live in Banaras like a Banarasi" means waking up to the sound of temple bells, 
              enjoying morning chai while watching the sunrise over the Ganges, navigating the vibrant 
              narrow lanes, experiencing the evening Ganga Aarti, savoring authentic street food, 
              and feeling the timeless spirituality that permeates every corner of this ancient city.
            </p>
            <p className="text-lg">
              At Ved Homestays, we don't just provide accommodation - we invite you to become part of 
              the Varanasi story, even if just for a few days. Come, be our guest, and take home 
              memories that last a lifetime.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
